import React, { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { AdminInput } from '../../components/admin/AdminUI';
import { Calendar, Check, ExternalLink, Loader2, RefreshCw, X, Bell } from 'lucide-react';

const Card = ({ children, className = '' }) => (
  <div
    className={`bg-[#0B132B] border border-[#B8860B]/20 rounded-lg p-6 ${className}`}
  >
    {children}
  </div>
);

const StatusPill = ({ connected }) => (
  <span
    data-testid="gcal-status-pill"
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
      connected
        ? 'bg-[#B8860B]/30 text-[#a8d196] border border-[#B8860B]/60'
        : 'bg-[#050814] text-[#F4F1EA]/60 border border-[#B8860B]/30'
    }`}
  >
    {connected ? <Check size={12} /> : <X size={12} />}
    {connected ? 'Connected' : 'Not connected'}
  </span>
);

export const AdminSettings = () => {
  const { authedAxios } = useAdminAuth();
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [selectedCal, setSelectedCal] = useState('');
  const [newCalName, setNewCalName] = useState('Creek Bottom Bookings');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const banner = (() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('gcal');
    if (!v) return null;
    if (v === 'connected')
      return { kind: 'ok', text: 'Google Calendar connected — pick a calendar below.' };
    return {
      kind: 'err',
      text: `Google Calendar connection failed (${params.get('reason') || 'unknown'}).`,
    };
  })();

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/google/calendar/status');
      setStatus(data);
      if (data.connected) {
        const { data: cals } = await authedAxios().get(
          '/api/admin/google/calendar/calendars',
        );
        setCalendars(cals.calendars || []);
        if (data.calendar_id) setSelectedCal(data.calendar_id);
      }
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to load calendar status');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnect = async () => {
    setBusy(true);
    setMsg('');
    try {
      const { data } = await authedAxios().get('/api/admin/google/calendar/connect');
      window.location.href = data.authorization_url;
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to start OAuth');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Future bookings will stop syncing.'))
      return;
    setBusy(true);
    try {
      await authedAxios().post('/api/admin/google/calendar/disconnect');
      setStatus({ connected: false });
      setCalendars([]);
      setSelectedCal('');
      setMsg('Disconnected.');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const handleSelectCalendar = async () => {
    setBusy(true);
    setMsg('');
    try {
      const body =
        selectedCal === '__new__' || !selectedCal
          ? { calendar_id: '__new__', new_calendar_name: newCalName }
          : { calendar_id: selectedCal };
      const { data } = await authedAxios().post(
        '/api/admin/google/calendar/select-calendar',
        body,
      );
      setMsg(`✓ Now syncing to "${data.calendar_name}"`);
      await loadStatus();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to select calendar');
    } finally {
      setBusy(false);
    }
  };

  const handleRunReminders = async () => {
    setBusy(true);
    setMsg('');
    try {
      await authedAxios().post('/api/admin/run-checkin-reminders');
      setMsg('✓ Check-in reminder job executed.');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Failed to run reminders');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-[#F4F1EA]">Settings</h1>
        <p className="text-sm text-[#F4F1EA]/60 mt-1">
          Manage integrations and automated tasks.
        </p>
      </div>

      {banner && (
        <div
          data-testid="gcal-banner"
          className={`px-4 py-3 rounded-md text-sm ${
            banner.kind === 'ok'
              ? 'bg-[#B8860B]/20 text-[#a8d196] border border-[#B8860B]/40'
              : 'bg-red-900/30 text-red-200 border border-red-800'
          }`}
        >
          {banner.text}
        </div>
      )}

      {/* Google Calendar */}
      <Card>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="text-[#B8860B]" size={26} />
            <div>
              <h2 className="text-xl font-semibold text-[#F4F1EA]">Google Calendar Sync</h2>
              <p className="text-sm text-[#F4F1EA]/60 mt-0.5">
                Automatically push new bookings and equipment rentals to a Google Calendar.
              </p>
            </div>
          </div>
          <StatusPill connected={!!status?.connected} />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#F4F1EA]/60 py-4">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : !status?.connected ? (
          <div className="space-y-3">
            <p className="text-sm text-[#F4F1EA]/75">
              Click below to connect a Google account. You'll be redirected to Google's
              authorization screen — this only needs to be done once. The connected account
              must be a <strong>test user</strong> (kjanephotos@gmail.com or
              dallas.scott2017@gmail.com) since the OAuth app is in "Testing" mode.
            </p>
            <Button
              data-testid="gcal-connect-btn"
              onClick={handleConnect}
              disabled={busy}
              className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#F4F1EA] font-semibold"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              Connect Google Calendar
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-[#B8860B] text-xs uppercase tracking-wider mb-1">
                  Connected account
                </p>
                <p data-testid="gcal-connected-email" className="text-[#F4F1EA]">
                  {status.email || '—'}
                </p>
              </div>
              <div>
                <p className="text-[#B8860B] text-xs uppercase tracking-wider mb-1">
                  Current calendar
                </p>
                <p data-testid="gcal-current-calendar" className="text-[#F4F1EA]">
                  {status.calendar_name || (
                    <span className="text-amber-300">⚠ No calendar selected yet</span>
                  )}
                </p>
              </div>
            </div>

            <div className="border-t border-[#B8860B]/15 pt-5 space-y-3">
              <p className="text-sm text-[#B8860B] font-semibold">
                {status.calendar_id ? 'Change target calendar' : 'Pick a calendar to sync to'}
              </p>
              <Select value={selectedCal} onValueChange={setSelectedCal}>
                <SelectTrigger
                  data-testid="gcal-calendar-select"
                  className="bg-[#050814] border-[#B8860B]/30 text-[#F4F1EA]"
                >
                  <SelectValue placeholder="Choose a calendar…" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B132B] border-[#B8860B]/30 text-[#F4F1EA]">
                  <SelectItem value="__new__">
                    + Create new calendar
                  </SelectItem>
                  {calendars.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.summary} {c.primary ? '(primary)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedCal === '__new__' && (
                <AdminInput
                  data-testid="gcal-new-calendar-name"
                  label="New calendar name"
                  value={newCalName}
                  onChange={(e) => setNewCalName(e.target.value)}
                />
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  data-testid="gcal-save-calendar"
                  onClick={handleSelectCalendar}
                  disabled={busy || !selectedCal}
                  className="bg-[#B8860B] hover:bg-[#E5C158] text-[#050814] font-semibold"
                >
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save calendar
                </Button>
                <Button
                  data-testid="gcal-refresh"
                  variant="outline"
                  onClick={loadStatus}
                  disabled={busy}
                  className="border-[#B8860B]/40 text-[#B8860B] hover:bg-[#B8860B] hover:text-[#050814]"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                </Button>
                <Button
                  data-testid="gcal-disconnect"
                  variant="outline"
                  onClick={handleDisconnect}
                  disabled={busy}
                  className="border-red-800 text-red-300 hover:bg-red-900/40"
                >
                  <X className="mr-2 h-4 w-4" /> Disconnect
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Reminders trigger */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <Bell className="text-[#B8860B]" size={26} />
          <div>
            <h2 className="text-xl font-semibold text-[#F4F1EA]">Check-in reminders</h2>
            <p className="text-sm text-[#F4F1EA]/60 mt-0.5">
              Reminder emails go out automatically every day at 9 AM Central for guests
              checking in <strong>7, 3, and 1 day</strong> away. Use the button below to
              run the job manually (idempotent — guests won't be emailed twice).
            </p>
          </div>
        </div>
        <Button
          data-testid="run-reminders-btn"
          onClick={handleRunReminders}
          disabled={busy}
          className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#F4F1EA] font-semibold"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
          Send pending reminders now
        </Button>
      </Card>

      {msg && (
        <div
          data-testid="settings-msg"
          className="px-4 py-3 rounded-md text-sm bg-[#050814] border border-[#B8860B]/30 text-[#F4F1EA]"
        >
          {msg}
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
